import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, ChevronDown, RefreshCw, Plus, Filter, X } from 'react-feather'
import { OrderInfo } from '../../hooks/useOrderBook'
import { OrderStatus, OrderType } from '../../constants/orderbook'
import { RowBetween } from '../../components/Row'
import { SectionTitle, EmptyState } from './styleds'
import type { OrderStatusFilter } from './index'
import OrderCard from './OrderCard'
import SyncOrderByTxModal from './SyncOrderByTxModal'
import Popover from '../../components/Popover'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import styled from 'styled-components'

// 参考 antd Simple 分页：左右箭头 + 中间 当前/总页
const PaginationWrap = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 12px;
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 6px;
  overflow: hidden;
  background: ${({ theme }) => theme.bg1};
`

const PaginationBtn = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-right: 1px solid ${({ theme }) => theme.bg3};
  background: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text1};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:last-of-type {
    border-right: none;
    border-left: 1px solid ${({ theme }) => theme.bg3};
  }
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.bg2};
  }
`

const PageInfo = styled.span`
  min-width: 64px;
  padding: 0 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.text1};
  text-align: center;
  line-height: 32px;
`

// 仿 antd Select：触发框 + 下拉面板
const TitleRow = styled(RowBetween)`
  margin: 12px 0 8px;
  align-items: center;
`

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.text2};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.bg2};
    color: ${({ theme }) => theme.text1};
  }
`

const BtnGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`

const AddressFilterPopoverContent = styled.div`
  padding: 12px;
  min-width: 280px;
`
const AddressFilterInputWrap = styled.div`
  position: relative;
  margin-bottom: 8px;
`
const AddressFilterInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 36px;
  padding: 0 36px 0 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.text1};
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 8px;
  outline: none;
  &::placeholder {
    color: ${({ theme }) => theme.text3};
  }
`
const AddressFilterClearBtn = styled.button`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.text2};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.bg3};
    color: ${({ theme }) => theme.text1};
  }
`
const AddressFilterBtn = styled.button`
  width: 100%;
  height: 32px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.white};
  background: ${({ theme }) => theme.primary1};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
`

const TypeFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
`
const TypeFilterLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.text2};
  flex-shrink: 0;
`
const TypeFilterSelect = styled.select`
  flex: 1;
  height: 36px;
  padding: 0 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.text1};
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 8px;
  cursor: pointer;
  outline: none;
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`
const SelectWrap = styled.div`
  margin-bottom: 12px;
  position: relative;
  flex: 1;
`

const SelectTrigger = styled.div<{ open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 0 12px 0 14px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
  background: ${({ theme }) => theme.bg2};
  border: 1px solid ${({ theme, open }) => (open ? theme.primary1 : theme.bg3)};
  border-radius: 8px;
  cursor: pointer;
  box-shadow: ${({ theme, open }) => (open ? `0 0 0 2px ${theme.primary1}20` : '0 1px 2px ' + theme.shadow1)};
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: ${({ theme, open }) => (open ? theme.primary1 : theme.bg4)};
  }
`

const SelectTriggerText = styled.span`
  flex: 1;
  text-align: left;
`

const SelectArrow = styled(ChevronDown)<{ open: boolean }>`
  flex-shrink: 0;
  color: ${({ theme }) => theme.text2};
  transition: transform 0.2s;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'none')};
`

const SelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 8px;
  box-shadow: 0 6px 16px ${({ theme }) => theme.shadow1};
  max-height: 256px;
  overflow-y: auto;
  padding: 4px 0;
`

const SelectOption = styled.div<{ disabled?: boolean; selected?: boolean }>`
  padding: 8px 14px;
  font-size: 14px;
  color: ${({ theme, disabled }) => (disabled ? theme.text3 : theme.text1)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  background: ${({ theme, selected }) => (selected ? theme.bg3 : 'transparent')};

  &:hover {
    background: ${({ theme, disabled }) => (disabled ? 'transparent' : theme.bg3)};
  }
`

const PLACEHOLDER_VALUE = '__placeholder__' // 仅作下拉内第一行提示，不可选

const STATUS_OPTIONS: { value: OrderStatusFilter | typeof PLACEHOLDER_VALUE; labelKey: string; disabled?: boolean }[] = [
  { value: PLACEHOLDER_VALUE, labelKey: 'orderFilterPlaceholder', disabled: true },
  { value: undefined, labelKey: 'orderFilterAll' },
  { value: OrderStatus.OPEN, labelKey: 'orderStatusOpen' },
  { value: OrderStatus.FILLED, labelKey: 'orderStatusFilled' },
  { value: OrderStatus.CANCELLED, labelKey: 'orderStatusCancelled' }
]

interface OrderListProps {
  orders: OrderInfo[]
  total: number
  page: number
  pageSize: number
  loading: boolean
  statusFilter: OrderStatusFilter
  onStatusFilterChange: (status: OrderStatusFilter) => void
  onPageChange: (page: number) => void
  onCancel: (orderId: number) => void
  onUpdatePrice: (orderId: number, currentPrice: string) => void
  onDetail: (orderId: number, isBuy?: boolean) => void
  /** 是否在卡片中显示用户地址（管理员所有订单用） */
  showAddress?: boolean
  cancellingId: number | null
  onRefresh?: () => void
  /** 为 true 时表示用户点击了刷新，请求中先清空列表并显示加载中 */
  isRefreshing?: boolean
  /** 列表标题 i18n key，如 orderMyOrders / orderAllOrdersTitle */
  listTitleKey?: string
  /** 是否显示状态下拉筛选（全部挂单不显示） */
  showStatusFilter?: boolean
  /** 嵌入管理页时：不包一层 padding，与上方白名单等区块对齐 */
  noOuterPadding?: boolean
  /** 嵌入管理页时：不渲染标题行文字，由外层 SectionTitle 提供 */
  hideTitle?: boolean
  /** 是否在卡片中显示编辑、取消按钮（管理员所有订单不显示） */
  showEditCancel?: boolean
  /** 是否显示按地址过滤（管理员所有订单用） */
  showAddressFilter?: boolean
  /** 当前地址过滤值 */
  addressFilter?: string
  /** 地址过滤变更回调 */
  onAddressFilterChange?: (address: string) => void
  /** 当前类型过滤：undefined=全部，0=买，1=卖 */
  typeFilter?: number | undefined
  /** 类型过滤变更回调 */
  onTypeFilterChange?: (type: number | undefined) => void
  /** 我的订单：在状态筛选同一行右侧显示买卖类型筛选 */
  showTypeFilterInRow?: boolean
}

export default function OrderList({
  orders,
  total,
  page,
  pageSize,
  loading,
  statusFilter,
  onStatusFilterChange,
  onPageChange,
  onCancel,
  onUpdatePrice,
  onDetail,
  cancellingId,
  onRefresh,
  isRefreshing,
  listTitleKey = 'orderMyOrders',
  showStatusFilter = true,
  showAddress = false,
  noOuterPadding = false,
  hideTitle = false,
  showEditCancel = true,
  showAddressFilter = false,
  addressFilter = '',
  onAddressFilterChange,
  typeFilter,
  onTypeFilterChange,
  showTypeFilterInRow = false
}: OrderListProps) {
  const { t } = useTranslation()
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0
  const [open, setOpen] = useState(false)
  const [typeRowOpen, setTypeRowOpen] = useState(false)
  const [syncByTxModalOpen, setSyncByTxModalOpen] = useState(false)
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)
  const [filterInputValue, setFilterInputValue] = useState(addressFilter)
  const [filterTypeValue, setFilterTypeValue] = useState<number | undefined>(typeFilter)
  const selectRef = useRef<HTMLDivElement>(null)
  const typeRowSelectRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(selectRef, () => setOpen(false))
  useOnClickOutside(typeRowSelectRef, () => setTypeRowOpen(false))

  const handleApplyFilter = () => {
    onAddressFilterChange?.(filterInputValue.trim())
    onTypeFilterChange?.(filterTypeValue)
    setFilterPopoverOpen(false)
  }

  React.useEffect(() => {
    setFilterInputValue(addressFilter)
  }, [addressFilter])

  React.useEffect(() => {
    setFilterTypeValue(typeFilter)
  }, [typeFilter])

  const displayLabel =
    statusFilter === undefined
      ? t('orderFilterAll')
      : STATUS_OPTIONS.find(o => o.value === statusFilter)?.labelKey
        ? t(STATUS_OPTIONS.find(o => o.value === statusFilter)!.labelKey)
        : t('orderFilterAll')

  const typeRowDisplayLabel =
    typeFilter === undefined ? t('orderFilterTypeAll') : typeFilter === OrderType.BUY ? t('orderBuy') : t('orderSell')

  return (
    <div style={noOuterPadding ? undefined : { padding: '0 0.75rem 0.75rem' }}>
      <TitleRow style={hideTitle ? { marginTop: 0 } : undefined}>
        {!hideTitle && (
          <SectionTitle style={{ margin: 0 }}>
            {t(listTitleKey)} {total > 0 && `(${total})`}
          </SectionTitle>
        )}
        {hideTitle && <span />}
        {onRefresh && (
          <BtnGroup>
            {showAddressFilter && (
              <Popover
                show={filterPopoverOpen}
                content={
                  <AddressFilterPopoverContent>
                    <AddressFilterInputWrap>
                      <AddressFilterInput
                        value={filterInputValue}
                        onChange={e => setFilterInputValue(e.target.value)}
                        placeholder={t('orderFilterAddressPlaceholder')}
                        onKeyDown={e => e.key === 'Enter' && handleApplyFilter()}
                      />
                      {filterInputValue && (
                        <AddressFilterClearBtn
                          type="button"
                          onClick={() => {
                            setFilterInputValue('')
                            onAddressFilterChange?.('')
                          }}
                          title={t('orderFilterAddressClear')}
                          aria-label={t('orderFilterAddressClear')}
                        >
                          <X size={14} />
                        </AddressFilterClearBtn>
                      )}
                    </AddressFilterInputWrap>
                    {showAddressFilter && !showTypeFilterInRow && (
                      <TypeFilterRow>
                        <TypeFilterLabel>{t('orderFilterTypeLabel')}</TypeFilterLabel>
                        <TypeFilterSelect
                          value={filterTypeValue === undefined ? '' : filterTypeValue}
                          onChange={e => setFilterTypeValue(e.target.value === '' ? undefined : Number(e.target.value))}
                        >
                          <option value="">{t('orderFilterTypeAll')}</option>
                          <option value={OrderType.BUY}>{t('orderBuy')}</option>
                          <option value={OrderType.SELL}>{t('orderSell')}</option>
                        </TypeFilterSelect>
                      </TypeFilterRow>
                    )}
                    <AddressFilterBtn type="button" onClick={handleApplyFilter}>
                      {t('orderFilterByAddressQuery')}
                    </AddressFilterBtn>
                  </AddressFilterPopoverContent>
                }
                placement="bottom-end"
              >
                <IconBtn
                  type="button"
                  onClick={() => {
                    setFilterInputValue(addressFilter)
                    setFilterTypeValue(typeFilter)
                    setFilterPopoverOpen(o => !o)
                  }}
                  title={t('orderFilterByAddress')}
                  aria-label={t('orderFilterByAddress')}
                  style={addressFilter || typeFilter !== undefined ? { color: 'var(--primary1)' } : undefined}
                >
                  <Filter size={14} />
                </IconBtn>
              </Popover>
            )}
            {showStatusFilter && (
              <IconBtn
                type="button"
                onClick={() => setSyncByTxModalOpen(true)}
                title={t('orderSyncByTxTitle')}
                aria-label={t('orderSyncByTxTitle')}
              >
                <Plus size={14} />
              </IconBtn>
            )}
            <IconBtn type="button" onClick={onRefresh} disabled={loading} title={t('orderRefresh')} aria-label={t('orderRefresh')}>
              <RefreshCw size={14} />
            </IconBtn>
          </BtnGroup>
        )}
      </TitleRow>

      {showStatusFilter && (
      <FilterRow style={showTypeFilterInRow ? {} : { display: 'block' }}>
        <SelectWrap ref={selectRef} style={showTypeFilterInRow ? { marginBottom: 0 } : undefined}>
          <SelectTrigger open={open} onClick={() => { setOpen(o => !o); showTypeFilterInRow && setTypeRowOpen(false) }} role="combobox" aria-expanded={open} aria-haspopup="listbox" aria-label={t('orderFilterPlaceholder')}>
            <SelectTriggerText>{displayLabel}</SelectTriggerText>
            <SelectArrow open={open} size={18} />
          </SelectTrigger>
          {open && (
            <SelectDropdown role="listbox">
              {STATUS_OPTIONS.map(({ value, labelKey, disabled }) => {
                const selected = value === statusFilter || (value === undefined && statusFilter === undefined)
                const isPlaceholder = value === PLACEHOLDER_VALUE
                return (
                  <SelectOption
                    key={value === undefined ? 'all' : String(value)}
                    disabled={!!disabled}
                    selected={selected && !isPlaceholder}
                    role="option"
                    aria-selected={selected && !isPlaceholder}
                    onClick={() => {
                      if (disabled) return
                      onStatusFilterChange(value === PLACEHOLDER_VALUE ? undefined : value)
                      setOpen(false)
                    }}
                  >
                    {t(labelKey)}
                  </SelectOption>
                )
              })}
            </SelectDropdown>
          )}
        </SelectWrap>
        {showTypeFilterInRow && onTypeFilterChange && (
          <SelectWrap ref={typeRowSelectRef} style={{ marginBottom: 0 }}>
            <SelectTrigger open={typeRowOpen} onClick={() => { setTypeRowOpen(o => !o); setOpen(false) }} role="combobox" aria-expanded={typeRowOpen} aria-haspopup="listbox" aria-label={t('orderFilterTypeLabel')}>
              <SelectTriggerText>{typeRowDisplayLabel}</SelectTriggerText>
              <SelectArrow open={typeRowOpen} size={18} />
            </SelectTrigger>
            {typeRowOpen && (
              <SelectDropdown role="listbox">
                <SelectOption
                  selected={typeFilter === undefined}
                  role="option"
                  aria-selected={typeFilter === undefined}
                  onClick={() => { onTypeFilterChange(undefined); setTypeRowOpen(false) }}
                >
                  {t('orderFilterTypeAll')}
                </SelectOption>
                <SelectOption
                  selected={typeFilter === OrderType.BUY}
                  role="option"
                  aria-selected={typeFilter === OrderType.BUY}
                  onClick={() => { onTypeFilterChange(OrderType.BUY); setTypeRowOpen(false) }}
                >
                  {t('orderBuy')}
                </SelectOption>
                <SelectOption
                  selected={typeFilter === OrderType.SELL}
                  role="option"
                  aria-selected={typeFilter === OrderType.SELL}
                  onClick={() => { onTypeFilterChange(OrderType.SELL); setTypeRowOpen(false) }}
                >
                  {t('orderSell')}
                </SelectOption>
              </SelectDropdown>
            )}
          </SelectWrap>
        )}
      </FilterRow>
      )}

      {loading && isRefreshing ? (
        <EmptyState>{t('orderLoading')}</EmptyState>
      ) : loading && orders.length === 0 ? (
        <EmptyState>{t('orderLoading')}</EmptyState>
      ) : orders.length === 0 ? (
        <EmptyState>{t('orderNoOrders')}</EmptyState>
      ) : (
        <>
          {orders.map(order => (
            <OrderCard
              key={order.orderId}
              order={order}
              onCancel={onCancel}
              onUpdatePrice={onUpdatePrice}
              onDetail={onDetail}
              cancellingId={cancellingId}
              showAddress={showAddress}
              showEditCancel={showEditCancel}
            />
          ))}

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <PaginationWrap>
                <PaginationBtn
                  type="button"
                  disabled={loading || page <= 1}
                  onClick={() => onPageChange(page - 1)}
                  aria-label={t('orderPrevPage')}
                >
                  <ChevronLeft size={18} />
                </PaginationBtn>
                <PageInfo>
                  {t('orderPageInfo', { current: page, total: totalPages })}
                </PageInfo>
                <PaginationBtn
                  type="button"
                  disabled={loading || page >= totalPages}
                  onClick={() => onPageChange(page + 1)}
                  aria-label={t('orderNextPage')}
                >
                  <ChevronRight size={18} />
                </PaginationBtn>
              </PaginationWrap>
            </div>
          )}
        </>
      )}

      <SyncOrderByTxModal
        isOpen={syncByTxModalOpen}
        onDismiss={() => setSyncByTxModalOpen(false)}
        onSuccess={() => onRefresh?.()}
      />
    </div>
  )
}
